import React from 'react';
import PropTypes from 'prop-types';
import { Trans } from 'react-i18next';
import useWhitelabelCopies from 'hooks/whitelabel/useWhitelabelCopies';
import { CurrentLang } from 'utils/lang';

const PRIMARY_HIGHLIGHT_CLASS = 'text-primary-regular font-bold';
const TERMS_LINK_CLASS = 'text-primary-regular underline hover:text-primary-strong';
const TERMS_FOOTNOTE_CLASS = 'mt-3 block text-left text-base text-[var(--gray-400)]';

/**
 * Whether the tenant provides custom promo copy for the download app modal.
 * @param {object} promoDownloadModal - Tenant promoDownloadModal env config.
 * @returns {boolean}
 */
export const hasPromoDownloadModalConfig = (promoDownloadModal) =>
  Boolean(promoDownloadModal?.text2?.trim());

/**
 * Whether the download app modal should render promo content (text and image).
 * @param {object} promoDownloadModal - Tenant promoDownloadModal env config.
 * @returns {boolean}
 */
export const isPromoDownloadModalActive = (promoDownloadModal) =>
  hasPromoDownloadModalConfig(promoDownloadModal);

/**
 * Resolves the modal image URL for the current presentation mode.
 * @param {object} params - Resolution params.
 * @param {boolean} params.isPromoModal - Whether promo mode is active.
 * @param {string} params.downloadAppImage - Tenant download app image URL.
 * @param {string} params.defaultPromoImage - Bundled promo image from the repository.
 * @param {string} params.defaultDownloadImage - Bundled download image fallback.
 * @returns {string}
 */
export const getAppDownloadModalImageUrl = ({
  isPromoModal,
  downloadAppImage,
  defaultPromoImage,
  defaultDownloadImage,
}) => {
  if (isPromoModal) {
    return defaultPromoImage;
  }

  return downloadAppImage?.trim() || defaultDownloadImage;
};

/**
 * Promo message for the download app modal.
 * Uses promoDownloadModal env (text1–text4) when configured; otherwise the default app download message.
 * text2 and text4 render with primary highlight styling.
 * termsAndConditionsUrl from tenant copies (policies) shows a localized terms line when set.
 * @param {object} props - Component props.
 * @param {object} props.promoDownloadModal - Tenant promo copy config.
 * @param {Function} props.t - i18n translate function (general namespace).
 * @returns {JSX.Element}
 */
const PromoDownloadModalMessage = ({ promoDownloadModal, t }) => {
  const { policies } = useWhitelabelCopies();
  const lang = CurrentLang();
  const termsAndConditionsUrl = policies?.[lang]?.termsAndConditionsUrl?.trim();

  if (!hasPromoDownloadModalConfig(promoDownloadModal)) {
    return (
      <Trans t={t} i18nKey="app_download_modal.message" as="p">
        <b />
      </Trans>
    );
  }

  const { text1 = '', text2, text3 = '', text4 } = promoDownloadModal;
  const showTermsLink = Boolean(termsAndConditionsUrl);

  const termsAndConditionsAnchor = showTermsLink ? (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/control-has-associated-label, jsx-a11y/anchor-has-content
    <a
      href={termsAndConditionsUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={TERMS_LINK_CLASS}
    />
  ) : null;

  return (
    <>
      <p>
        {text1}
        <span className={PRIMARY_HIGHLIGHT_CLASS}>{text2}</span>
        {text3}
        {text4?.trim() ? <span className={PRIMARY_HIGHLIGHT_CLASS}>{text4}</span> : null}
      </p>
      {showTermsLink ? (
        <small className={TERMS_FOOTNOTE_CLASS}>
          <Trans t={t} i18nKey="app_download_modal.terms">
            {termsAndConditionsAnchor}
          </Trans>
        </small>
      ) : null}
    </>
  );
};

PromoDownloadModalMessage.propTypes = {
  promoDownloadModal: PropTypes.shape({
    text1: PropTypes.string,
    text2: PropTypes.string,
    text3: PropTypes.string,
    text4: PropTypes.string,
  }),
  t: PropTypes.func.isRequired,
};

PromoDownloadModalMessage.defaultProps = {
  promoDownloadModal: {},
};

export default PromoDownloadModalMessage;
